/**
 * Story Navigation Bar
 * UI with dots + arrows for panel navigation
 * Visibility controlled by data-story-nav attribute
 * Note: Uses IIFE pattern for Ghost's concatenated build system
 */

(function (window) {
  'use strict';

  class StoryNav {
    constructor() {
      this.container = null;
      this.dotsContainer = null;
      this.prevButton = null;
      this.nextButton = null;
      this.dots = [];

      this.currentIndex = -1;
      this.totalPanels = 0;
      this.isHidden = false;

      // Hub mode: detected via DOM, replaces dots with progress UI
      this.hubMode = false;
      this.hubIndex = -1;
      this.progressElement = null;
      this.hubButton = null;

      this.init();
    }

    init() {
      // Wait for StoryAPI to be available
      if (typeof window.StoryAPI === 'undefined') {
        console.warn('StoryNav: StoryAPI not found. Story Bar disabled.');
        return;
      }

      // Check if globally disabled
      if (document.body.getAttribute('data-story-nav') === 'off') {
        return;
      }

      // Find or create container
      this.container = document.getElementById('story-nav');
      if (!this.container) {
        console.warn('StoryNav: Container #story-nav not found.');
        return;
      }

      // Detect hub mode from DOM
      var hubPanel = document.querySelector('[data-hub]');
      if (hubPanel) {
        this.hubMode = true;
        // Find hub index in StoryAPI panels
        var panels = window.StoryAPI.getPanels();
        for (var i = 0; i < panels.length; i++) {
          if (panels[i].hasAttribute('data-hub')) {
            this.hubIndex = i;
            break;
          }
        }
      }

      // Build UI (different for hub mode vs normal)
      if (this.hubMode) {
        this.buildHubUI();
      } else {
        this.buildUI();
      }

      // Listen to panel activation events
      this.setupEventListeners();

      // Initial update
      this.update();
    }

    // --- Normal Mode UI (unchanged) ---

    buildUI() {
      // Clear existing content
      this.container.innerHTML = '';

      // Create previous arrow
      this.prevButton = document.createElement('button');
      this.prevButton.className = 'story-nav__arrow story-nav__arrow--prev';
      this.prevButton.setAttribute('aria-label', 'Previous panel');
      this.prevButton.addEventListener('click', this.handlePrev.bind(this));
      this.container.appendChild(this.prevButton);

      // Create dots container
      this.dotsContainer = document.createElement('ul');
      this.dotsContainer.className = 'story-nav__dots';
      this.dotsContainer.setAttribute('role', 'navigation');
      this.dotsContainer.setAttribute('aria-label', 'Story panels');
      this.container.appendChild(this.dotsContainer);

      // Create next arrow
      this.nextButton = document.createElement('button');
      this.nextButton.className = 'story-nav__arrow story-nav__arrow--next';
      this.nextButton.setAttribute('aria-label', 'Next panel');
      this.nextButton.addEventListener('click', this.handleNext.bind(this));
      this.container.appendChild(this.nextButton);

      // Create dots for each panel
      this.createDots();
    }

    createDots() {
      var panels = window.StoryAPI.getPanels();
      this.totalPanels = panels.length;

      // Clear existing dots
      this.dotsContainer.innerHTML = '';
      this.dots = [];

      var self = this;
      panels.forEach(function(panel, index) {
        var li = document.createElement('li');

        var button = document.createElement('button');
        button.className = 'story-nav__dot';
        button.setAttribute('aria-label', 'Go to panel ' + (index + 1));
        button.setAttribute('data-index', index);

        button.addEventListener('click', function() {
          self.handleDotClick(index);
        });

        li.appendChild(button);
        self.dotsContainer.appendChild(li);
        self.dots.push(button);
      });
    }

    // --- Hub Mode UI ---

    buildHubUI() {
      this.container.innerHTML = '';

      var self = this;

      // Hub button — navigates back to hub via fadeTo
      this.hubButton = document.createElement('button');
      this.hubButton.className = 'story-nav__hub-btn';
      this.hubButton.setAttribute('aria-label', 'Back to Hub');
      this.hubButton.addEventListener('click', function() {
        // Debounce: skip if already at hub or navigation in progress
        if (window.storyActiveIndex === self.hubIndex) return;
        if (window.StoryHubNav) {
          window.StoryHubNav.returnToHub();
        } else if (self.hubIndex >= 0 && window.StoryAPI && window.StoryAPI.fadeTo) {
          window.StoryAPI.fadeTo(self.hubIndex);
        }
      });
      this.container.appendChild(this.hubButton);

      // Progress element (replaces dots)
      this.progressElement = document.createElement('span');
      this.progressElement.className = 'story-nav__progress';
      this.progressElement.setAttribute('aria-live', 'polite');
      this.progressElement.textContent = '0/0';
      this.container.appendChild(this.progressElement);
    }

    updateProgress(visitedCount, totalChapters) {
      if (this.progressElement) {
        this.progressElement.textContent = visitedCount + '/' + totalChapters;
      }
    }

    // --- Event Listeners ---

    setupEventListeners() {
      var self = this;

      // Listen to panel activated event
      document.addEventListener('story:panel:activated', function(e) {
        self.currentIndex = e.detail.index;
        self.update();
      });

      // Check per-panel visibility (data-story-nav="off")
      document.addEventListener('story:panel:activated', function(e) {
        var panel = e.detail.panel;
        var navSetting = panel.getAttribute('data-story-nav');

        if (navSetting === 'off') {
          self.hide();
        } else {
          self.show();
        }
      });

      // Hub mode: listen for progress updates
      if (self.hubMode) {
        document.addEventListener('story:hub:updated', function(e) {
          self.updateProgress(e.detail.visitedCount, e.detail.totalChapters);
        });
      }
    }

    // --- Update ---

    update() {
      // Hub mode: no dots/arrows to update
      if (this.hubMode) return;

      // Update dots
      this.dots.forEach(function(dot, index) {
        if (index === this.currentIndex) {
          dot.classList.add('is-active');
        } else {
          dot.classList.remove('is-active');
        }
      }, this);

      // Update arrow states
      this.updateArrows();
    }

    updateArrows() {
      if (!this.prevButton || !this.nextButton) return;

      // Disable prev arrow at first panel
      if (this.currentIndex <= 0) {
        this.prevButton.disabled = true;
      } else {
        this.prevButton.disabled = false;
      }

      // Disable next arrow at last panel
      if (this.currentIndex >= this.totalPanels - 1) {
        this.nextButton.disabled = true;
      } else {
        this.nextButton.disabled = false;
      }
    }

    // --- Handlers (normal mode only) ---

    handleDotClick(index) {
      if (this.hubMode) return;
      window.StoryAPI.goto(index);
    }

    handlePrev() {
      if (this.hubMode) return;
      window.StoryAPI.prev();
    }

    handleNext() {
      if (this.hubMode) return;
      window.StoryAPI.next();
    }

    hide() {
      if (!this.isHidden) {
        this.container.classList.add('is-hidden');
        this.isHidden = true;
      }
    }

    show() {
      if (this.isHidden) {
        this.container.classList.remove('is-hidden');
        this.isHidden = false;
      }
    }

    destroy() {
      if (this.container) {
        this.container.innerHTML = '';
      }
      this.dots = [];
      this.progressElement = null;
      this.hubButton = null;
    }
  }

  // Auto-initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      new StoryNav();
    });
  } else {
    new StoryNav();
  }

  // Expose for manual initialization if needed
  window.StoryNav = StoryNav;

})(window);
